/**
 * Discount calculations: percentage off, fixed amount off, reverse, stacked.
 */

export interface DiscountResult {
  original: number;
  discount: number;
  finalPrice: number;
  effectivePct: number;
}

export function percentOff(original: number, pct: number): DiscountResult {
  const o = Math.max(0, original || 0);
  const p = Math.max(0, Math.min(100, pct || 0));
  const discount = o * (p / 100);
  return { original: o, discount, finalPrice: o - discount, effectivePct: p };
}

export function amountOff(original: number, amt: number): DiscountResult {
  const o = Math.max(0, original || 0);
  const a = Math.max(0, Math.min(o, amt || 0));
  return {
    original: o,
    discount: a,
    finalPrice: o - a,
    effectivePct: o > 0 ? (a / o) * 100 : 0,
  };
}

export function reverseDiscount(original: number, finalP: number): DiscountResult {
  const o = Math.max(0, original || 0);
  const f = Math.max(0, Math.min(o, finalP || 0));
  const discount = o - f;
  return {
    original: o,
    discount,
    finalPrice: f,
    effectivePct: o > 0 ? (discount / o) * 100 : 0,
  };
}

export interface StackedStep {
  pct: number;
  before: number;
  saved: number;
  after: number;
}

export interface StackedResult extends DiscountResult {
  steps: StackedStep[];
}

export function stackedDiscount(original: number, pcts: number[]): StackedResult {
  const o = Math.max(0, original || 0);
  let cur = o;
  const steps: StackedStep[] = [];
  for (const raw of pcts) {
    const p = Math.max(0, Math.min(100, raw || 0));
    const before = cur;
    const after = before * (1 - p / 100);
    steps.push({ pct: p, before, saved: before - after, after });
    cur = after;
  }
  return {
    original: o,
    discount: o - cur,
    finalPrice: cur,
    effectivePct: o > 0 ? ((o - cur) / o) * 100 : 0,
    steps,
  };
}

export function applyVat(amount: number, vatPct: number): { vat: number; gross: number } {
  const a = Math.max(0, amount || 0);
  const v = Math.max(0, vatPct || 0);
  const vat = a * (v / 100);
  return { vat, gross: a + vat };
}
